import React, { useState } from 'react';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import arrayMove from 'array-move';
import { Icon, Menu, MenuItem } from 'components';
import { PhotoFileType } from '../../types';
import PhotoPreviewModal, { PreviewPhoto } from './PhotoPreviewModal';
import {
  StyledPhoto,
  StyledUtilityContainer,
  StyledUtilityButton,
  StyledPhotoGrid,
  StyledDropdown,
} from './StyledComponents';

interface PhotosGridProps {
  photos: PhotoFileType[];
  setPhotos: React.Dispatch<React.SetStateAction<PhotoFileType[]>>;
  setDeletedPhotos: React.Dispatch<React.SetStateAction<PhotoFileType[]>>;
  thumbnail: PhotoFileType | null;
  setThumbnail: React.Dispatch<React.SetStateAction<PhotoFileType | null>>;
}

const PhotosGrid: React.FC<PhotosGridProps> = ({
  photos,
  setPhotos,
  setDeletedPhotos,
  thumbnail,
  setThumbnail,
}) => {
  const [previewImage, setPreviewImage] = useState<PreviewPhoto | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const onSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    setPhotos(arrayMove(photos, oldIndex, newIndex));
  };

  const closePreview = () => {
    setPreviewImage(null);
    setPreviewOpen(false);
  };

  const onPreview = (photo: PhotoFileType) => {
    if (photo?.filename && photo?.url) {
      setPreviewImage({ name: photo.filename, url: photo.url });
      setPreviewOpen(true);
    }
  };

  const removePhoto = (photo: PhotoFileType) => {
    const { id: photoId } = photo;

    // APP-5216 If Id exists, delete based on ID generated by server, otherwise delete based on URL generated upon upload.
    const newPhotos = photoId
      ? photos.filter(({ id }) => id !== photo.id)
      : photos.filter(({ url }) => url !== photo.url);

    if (!newPhotos.length) {
      setThumbnail(null);
    } else if (thumbnail?.url === photo.url) {
      setThumbnail(newPhotos[0]);
    }

    setPhotos(newPhotos);
    setDeletedPhotos(prev => [
      ...prev,
      { ...photo, isDeleted: true, status: 'removed' },
    ]);
  };

  return (
    <>
      <SortableList
        photos={photos}
        removePhoto={removePhoto}
        setThumbnail={setThumbnail}
        onSortEnd={onSortEnd}
        onPreview={onPreview}
        axis="xy"
      />
      <PhotoPreviewModal
        visible={previewOpen}
        closeModal={closePreview}
        previewImage={previewImage}
      />
    </>
  );
};

const SortableItem = SortableElement(
  ({
    photo,
    removePhoto,
    setThumbnail,
    onPreview,
  }: {
    photo: PhotoFileType;
    removePhoto: (p: PhotoFileType) => void;
    setThumbnail: (thumbnail: PhotoFileType) => void;
    onPreview: (p: PhotoFileType) => void;
  }) => {
    const [popoverVisible, setPopoverVisible] = useState(false);

    return (
      <StyledPhoto $imageUrl={photo.url}>
        <StyledUtilityContainer>
          <StyledUtilityButton $isDrag>
            <Icon type="arrowCross" color="white" width={11} offset={[0, -1]} />
          </StyledUtilityButton>
          <StyledDropdown
            placement="bottomRight"
            dropdownRender={() => (
              <Menu onClick={() => setPopoverVisible(false)}>
                <MenuItem key={'thumbnail'} onClick={() => setThumbnail(photo)}>
                  Use as thumbnail
                </MenuItem>
                <MenuItem key={'preview'} onClick={() => onPreview(photo)}>
                  Preview
                </MenuItem>
                <MenuItem key={'delete'} onClick={() => removePhoto(photo)}>
                  Delete
                </MenuItem>
              </Menu>
            )}
            trigger={['click']}
            open={popoverVisible}
            onOpenChange={visible => setPopoverVisible(visible)}
            arrow
          >
            <StyledUtilityButton onClick={e => e.stopPropagation()}>
              <Icon type="ellipsis" color="white" width={12} />
            </StyledUtilityButton>
          </StyledDropdown>
        </StyledUtilityContainer>
      </StyledPhoto>
    );
  },
);

const SortableList = SortableContainer(
  ({
    photos,
    removePhoto,
    setThumbnail,
    onPreview,
  }: {
    photos: PhotoFileType[];
    removePhoto: (photo: PhotoFileType) => void;
    setThumbnail: (thumbnail: PhotoFileType) => void;
    onPreview: (photo: PhotoFileType) => void;
  }) => (
      <StyledPhotoGrid>
        {photos.map((photo, index) => (
          <SortableItem
            key={`listing-photo-${index}`}
            index={index}
            photo={photo}
            removePhoto={removePhoto}
            setThumbnail={setThumbnail}
            onPreview={onPreview}
          />
        ))}
      </StyledPhotoGrid>
    ),
);

export default PhotosGrid;
